import React, { useEffect, useState } from "react";
import { api, useAxiosInterceptor } from "../shared/axiosInstance";

const DiscountCodeManager = () => {
    const [codes, setCodes] = useState([]);
    const [newCode, setNewCode] = useState("");
    const [discountPercent, setDiscountPercent] = useState(5);
    const [commissionPercent, setCommissionPercent] = useState(20);
    const [message, setMessage] = useState("");

    const [userSearch, setUserSearch] = useState("");
    const [userOptions, setUserOptions] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState("");
    const [selectedUserDisplay, setSelectedUserDisplay] = useState("");

    useAxiosInterceptor();

    const fetchCodes = async () => {
        try {
            const res = await api.get("/admin/discount-codes");
            setCodes(res.data);
        } catch (err) {
            console.error("❌ Failed to fetch codes:", err);
        }
    };

    useEffect(() => {
        fetchCodes();
    }, []);

    // Debounced user search
    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            const search = userSearch.trim();
            if (search.length < 2) {
                setUserOptions([]);
                return;
            }

            api.get(`/admin/users?search=${encodeURIComponent(search)}`)
                .then((res) => setUserOptions(res.data))
                .catch((err) => console.error("❌ Failed to search users:", err));
        }, 300);

        return () => clearTimeout(delayDebounce);
    }, [userSearch]);

    const handleCreate = async () => {
        try {
            await api.post("/admin/discount-codes", {
                code: newCode,
                discountPercent,
                commissionPercent,
                userId: selectedUserId,
            });
            setMessage("✅ Discount code created");
            setNewCode("");
            setUserSearch("");
            setSelectedUserId("");
            setSelectedUserDisplay("");
            fetchCodes();
        } catch (err) {
            setMessage("❌ Error creating code");
        }
    };

    const handleDelete = async (codeId) => {
        try {
            await api.delete(`/admin/discount-codes/${codeId}`);
            fetchCodes();
        } catch (err) {
            console.error("❌ Failed to delete code:", err);
        }
    };

    return (
        <div className="p-4">
            <h2 className="text-xl font-bold text-orange mb-4">Manage Discount Codes</h2>

            <div className="flex items-end flex-wrap gap-4 mb-6">
                <div className="flex flex-col">
                    <label className="text-sm mb-1 text-lightgray">Code</label>
                    <input
                        type="text"
                        placeholder="SPRING2024"
                        value={newCode}
                        onChange={(e) => setNewCode(e.target.value)}
                        className="p-2 rounded border bg-deepblue border-lightblue text-lightgray"
                    />
                </div>

                <div className="flex flex-col">
                    <label className="text-sm mb-1 text-lightgray">Discount %</label>
                    <input
                        type="number"
                        value={discountPercent}
                        min={0}
                        max={100}
                        onChange={(e) => setDiscountPercent(Number(e.target.value))}
                        className="p-2 rounded border bg-deepblue border-lightblue text-lightgray w-24"
                    />
                </div>

                <div className="flex flex-col">
                    <label className="text-sm mb-1 text-lightgray">Commission %</label>
                    <input
                        type="number"
                        value={commissionPercent}
                        min={0}
                        max={100}
                        onChange={(e) => setCommissionPercent(Number(e.target.value))}
                        className="p-2 rounded border bg-deepblue border-lightblue text-lightgray w-24"
                    />
                </div>

                <div className="flex flex-col relative w-64">
                    <label className="text-sm mb-1 text-lightgray">Affiliate User</label>
                    <input
                        type="text"
                        placeholder="Search by name or email"
                        value={userSearch}
                        onChange={(e) => {
                            setUserSearch(e.target.value);
                            setSelectedUserId("");
                        }}
                        className="p-2 rounded border bg-deepblue border-lightblue text-lightgray"
                    />
                    {userOptions.length > 0 && (
                        <ul className="absolute z-10 bg-deepblue border border-lightblue mt-1 rounded shadow w-full max-h-48 overflow-y-auto">
                            {userOptions.map((user) => {
                                const label = `${user.firstName} ${user.lastName} (${user.email})`;
                                return (
                                    <li
                                        key={user._id}
                                        onClick={() => {
                                            setSelectedUserId(user._id);
                                            setSelectedUserDisplay(label);
                                            setUserSearch(label);
                                            setUserOptions([]);
                                        }}
                                        className="p-2 hover:bg-bluegray cursor-pointer text-lightgray"
                                    >
                                        {label}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                <button
                    onClick={handleCreate}
                    disabled={!selectedUserId || !newCode}
                    className="h-[42px] mt-[22px] bg-orange text-darkblue font-bold py-2 px-4 rounded hover:bg-deeporange disabled:opacity-50"
                >
                    Add Code
                </button>
            </div>

            {message && <p className="text-lightgray mb-4">{message}</p>}

            <table className="w-full text-sm text-left">
                <thead className="text-xs uppercase text-lightblue">
                <tr>
                    <th>Code</th>
                    <th>Discount %</th>
                    <th>Commission %</th>
                    <th>Actions</th>
                </tr>
                </thead>
                <tbody>
                {codes.map((code) => (
                    <tr key={code._id} className="border-b border-lightblue text-lightgray">
                        <td>{code.code}</td>
                        <td>{code.discountPercent}%</td>
                        <td>{code.commissionPercent}%</td>
                        <td>
                            <button
                                onClick={() => handleDelete(code._id)}
                                className="text-red-400 hover:underline"
                            >
                                Delete
                            </button>
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>
            <CommissionTable />
        </div>
    );
};

export default DiscountCodeManager;

const CommissionTable = () => {
    const [commissions, setCommissions] = useState([]);
    const [filters, setFilters] = useState({
        transactionId: "",
        email: "",
        firstName: "",
        lastName: "",
        unpaidOnly: false,
    });
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const fetchCommissions = async () => {
        try {
            const params = new URLSearchParams();
            if (filters.transactionId) params.append("transaction", filters.transactionId);
            if (filters.email) params.append("email", filters.email);
            if (filters.firstName) params.append("firstName", filters.firstName);
            if (filters.lastName) params.append("lastName", filters.lastName);
            if (filters.unpaidOnly) params.append("unpaidOnly", "true");
            params.append("page", page);
            const res = await api.get(`/admin/commissions?${params.toString()}`);
            setCommissions(res.data.results);
            setTotalPages(res.data.totalPages);
        } catch (err) {
            console.error("❌ Failed to fetch commissions:", err);
        }
    };

    useEffect(() => {
        fetchCommissions();
    }, [filters, page, fetchCommissions]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setPage(1); // reset page on filter change
        setFilters((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    return (
        <div className="mt-12">
            <h2 className="text-xl font-bold text-orange mb-4">Commission Records</h2>

            {/* Filters */}
            <div className="flex flex-wrap gap-4 mb-4">
                <input
                    type="text"
                    name="transactionId"
                    value={filters.transactionId}
                    onChange={handleChange}
                    placeholder="Transaction ID"
                    className="p-2 rounded border bg-deepblue border-lightblue text-lightgray"
                />
                <input
                    type="text"
                    name="email"
                    value={filters.email}
                    onChange={handleChange}
                    placeholder="Affiliate Email"
                    className="p-2 rounded border bg-deepblue border-lightblue text-lightgray"
                />
                <input
                    type="text"
                    name="firstName"
                    value={filters.firstName}
                    onChange={handleChange}
                    placeholder="Affiliate First Name"
                    className="p-2 rounded border bg-deepblue border-lightblue text-lightgray"
                />
                <input
                    type="text"
                    name="lastName"
                    value={filters.lastName}
                    onChange={handleChange}
                    placeholder="Affiliate Last Name"
                    className="p-2 rounded border bg-deepblue border-lightblue text-lightgray"
                />
                <label className="flex items-center text-lightgray space-x-2">
                    <input
                        type="checkbox"
                        name="unpaidOnly"
                        checked={filters.unpaidOnly}
                        onChange={handleChange}
                        className="form-checkbox"
                    />
                    <span>Unpaid Only</span>
                </label>
            </div>

            {/* Table */}
            <table className="w-full text-sm text-left mb-2">
                <thead className="text-xs uppercase text-lightblue">
                <tr>
                    <th>Transaction</th>
                    <th>Affiliate</th>
                    <th>Amount Earned</th>
                    <th>Amount Paid</th>
                    <th>Paid At</th>
                </tr>
                </thead>
                <tbody>
                {commissions.map((c) => (
                    <tr key={c._id} className="border-b border-lightblue text-lightgray">
                        <td>{c.transaction}</td>
                        <td>
                            {c.affiliate?.firstName} {c.affiliate?.lastName}<br />
                            <span className="text-xs text-lightblue">{c.affiliate?.email}</span>
                        </td>
                        <td>${(c.amountEarned / 100).toFixed(2)}</td>
                        <td>${(c.amountPaid / 100).toFixed(2)}</td>
                        <td>{c.paidAt ? new Date(c.paidAt).toLocaleDateString() : "—"}</td>
                    </tr>
                ))}
                </tbody>
            </table>

            {/* Pagination */}
            <div className="flex justify-center space-x-4 text-lightgray mt-2">
                <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 rounded bg-deepblue border border-lightblue hover:bg-bluegray disabled:opacity-50"
                >
                    Prev
                </button>
                <span className="px-4">Page {page} of {totalPages}</span>
                <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1 rounded bg-deepblue border border-lightblue hover:bg-bluegray disabled:opacity-50"
                >
                    Next
                </button>
            </div>
        </div>
    );
};
